import { useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { authFetch } from '../api/authFetch';
import { UploadModal } from './UploadModal';

/** Geometric folder icon — minimalistic, matches design system */
const FolderIcon = () => (
  <svg width="0.9em" height="0.9em" viewBox="0 0 24 24" fill="currentColor"
       aria-hidden="true" style="vertical-align:middle;flex-shrink:0;display:inline-block">
    <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
  </svg>
);
import type { FileTreeGroup, WorkspaceTreeResponse } from '../../../shared/src/index';
import { useFileDrawerStore } from '../state/fileDrawerStore';
import {
  useUiState,
  getUiState,
  toggleFileDrawerCollapsedProject,
  toggleFileDrawerCollapsedSection,
  setFileDrawerOpenSections,
  setFileDrawerScroll,
  setFileDrawerUploadProject,
} from '../state/uiStateStore';
import { useSwipeDownToClose } from '../hooks/useSwipeDownToClose';

interface FileEntry {
  name: string;
  path: string;
  size: number;
  modifiedAt: string;
}

/** One section in a multi-section unified drawer */
export interface SectionDef {
  id: string;
  title: string;
  icon: string;
  emptyIcon?: string;
  /** tree mode: scan workspace for dirs named dirName */
  dirName?: string;
  /** flat mode: list files in a single directory */
  path?: string;
  configureHint?: string;
}

/** Config for workspace-centric mode — project wrappers with all section types inside.
 *  Archive is derived automatically from _archive/ subdirs within each section dir
 *  (matches the /plan skill convention: plans/_archive/ → Archived Plans). */
export interface WorkspaceSections {
  postsDirName?: string;
  opDocsDirName?: string;
  plansDirName?: string;
  projectDocsDirName?: string;
  registryDirName?: string;
  /** Directory name for per-project uploads (converted Markdown files). Shows upload button. */
  uploadsDirName?: string;
}

interface Props {
  open: boolean;
  title: string;
  icon: string;
  emptyIcon?: string;
  onClose: () => void;
  onConfigurePath: () => void;
  configureHint?: string;
  // flat mode: list files in a single directory
  path?: string;
  // tree mode: scan workspace for dirs named dirName and group by project
  dirName?: string;
  /** Multi-section mode: renders multiple collapsible sections in one drawer */
  sections?: SectionDef[];
  /** Workspace-centric mode: project wrappers each containing all section types */
  workspaceSections?: WorkspaceSections;
}

/** A file node is either a direct file or a collapsible subdir group */
type FileNode =
  | { kind: 'file'; file: FileEntry }
  | { kind: 'subdir'; name: string; files: FileEntry[] };

/** Group files that share a first path segment into collapsible subdir nodes */
function groupBySubdir(files: FileEntry[]): FileNode[] {
  const result: FileNode[] = [];
  const subdirMap = new Map<string, FileEntry[]>();
  for (const f of files) {
    const slash = f.name.indexOf('/');
    if (slash === -1) {
      result.push({ kind: 'file', file: f });
    } else {
      const subdir = f.name.substring(0, slash);
      const subFile: FileEntry = { ...f, name: f.name.substring(slash + 1) };
      if (!subdirMap.has(subdir)) {
        const arr: FileEntry[] = [];
        subdirMap.set(subdir, arr);
        result.push({ kind: 'subdir', name: subdir, files: arr });
      }
      subdirMap.get(subdir)!.push(subFile);
    }
  }
  return result;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function timeAgo(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ShareOptionsSheet and standalone share functions removed in PR #543 —
   all share functionality unified into ShareTargetModal via valko-share events. */

const PROJECTS_ROOT = '/home/akrsmv/projects/';

function CopyBtn({ filePath }: { filePath: string }) {
  const [copied, setCopied] = useState(false);

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    const rel = filePath.startsWith(PROJECTS_ROOT) ? filePath.slice(PROJECTS_ROOT.length) : filePath;
    navigator.clipboard.writeText(rel).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <button
      class={`tdrawer-file-copy${copied ? ' copied' : ''}`}
      title={copied ? 'Copied!' : 'Copy relative path'}
      onClick={handleClick}
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

function ShareBtn({ filePath, fileName }: { filePath: string; fileName: string }) {
  const isMarkdown = fileName.endsWith('.md');
  const isPdfCapable = isMarkdown || fileName.endsWith('.html') || fileName.endsWith('.htm');

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('valko-share', {
      detail: {
        type: 'file',
        filePath,
        fileName,
        pdfCapable: isPdfCapable,
        mediaEmbeddable: isMarkdown,
      },
    }));
  };

  return (
    <button
      class="tdrawer-file-share"
      title="Share file"
      onClick={handleClick}
    >↗</button>
  );
}

function FileRow({ f, onOpen, active }: { f: FileEntry; onOpen: (path: string) => void; active?: boolean }) {
  return (
    <li class={`tdrawer-file${active ? ' tdrawer-file--active' : ''}`} onClick={() => onOpen(f.path)}>
      <CopyBtn filePath={f.path} />
      <ShareBtn filePath={f.path} fileName={f.name} />
      <div class="tdrawer-file-info">
        <span class="tdrawer-file-name">{f.name}</span>
        <span class="tdrawer-file-meta">{formatSize(f.size)} · {timeAgo(f.modifiedAt)}</span>
      </div>
    </li>
  );
}

/**
 * Upload file row: FileRow + a 🗑 delete button (Documents section only).
 * Deletes the uploaded `.md` AND its original PDF via DELETE /files/upload.
 * Two-tap confirm (🗑 → "Sure?" → delete) — mobile-friendly, no modal needed.
 * NOT folded into the shared FileRow: that row is reused for plans/docs/archive,
 * which must NOT be deletable from here. Mirrors the PostFileRow precedent.
 */
function UploadFileRow({ f, onOpen, active, onDeleted }: { f: FileEntry; onOpen: (path: string) => void; active?: boolean; onDeleted: () => void }) {
  const [delState, setDelState] = useState<'idle' | 'confirm' | 'deleting' | 'error'>('idle');

  const handleDelete = useCallback(async (e: MouseEvent) => {
    e.stopPropagation();
    if (delState === 'idle') {
      setDelState('confirm');
      // Auto-revert the confirm prompt if the user doesn't follow through.
      setTimeout(() => setDelState((s) => (s === 'confirm' ? 'idle' : s)), 3000);
      return;
    }
    if (delState !== 'confirm') return;
    setDelState('deleting');
    try {
      const res = await authFetch(`/api/files/upload?path=${encodeURIComponent(f.path)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      onDeleted();
    } catch {
      setDelState('error');
      setTimeout(() => setDelState('idle'), 2000);
    }
  }, [delState, f.path, onDeleted]);

  return (
    <li class={`tdrawer-file${active ? ' tdrawer-file--active' : ''}`} onClick={() => onOpen(f.path)}>
      <CopyBtn filePath={f.path} />
      <ShareBtn filePath={f.path} fileName={f.name} />
      <div class="tdrawer-file-info">
        <span class="tdrawer-file-name">{f.name}</span>
        <span class="tdrawer-file-meta">{formatSize(f.size)} · {timeAgo(f.modifiedAt)}</span>
      </div>
      <button
        class={`upload-delete-btn${delState === 'confirm' ? ' confirm' : ''}${delState === 'error' ? ' error' : ''}`}
        title={delState === 'confirm' ? 'Tap again to delete' : delState === 'error' ? 'Delete failed' : 'Delete document'}
        aria-label="Delete document"
        onClick={handleDelete}
      >
        {delState === 'deleting' ? '…' : delState === 'confirm' ? 'Sure?' : delState === 'error' ? '✕' : '🗑'}
      </button>
    </li>
  );
}

/** Post file row: same as FileRow but with a "↩ Source" button that navigates to the originating session. */
function PostFileRow({ f, onOpen, active }: { f: { name: string; path: string; size: number; modifiedAt: string }; onOpen: (path: string) => void; active?: boolean }) {
  const [sourceState, setSourceState] = useState<'idle' | 'loading' | 'error'>('idle');

  const handleSource = useCallback(async (e: MouseEvent) => {
    e.stopPropagation();
    setSourceState('loading');
    try {
      const res = await authFetch(`/api/files/read?path=${encodeURIComponent(f.path)}&full=1`);
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const content: string = (data.lines as string[]).join('\n');
      // Parse YAML frontmatter: ---\n...\n---
      const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
      if (!fmMatch) { setSourceState('error'); setTimeout(() => setSourceState('idle'), 2000); return; }
      const fm = fmMatch[1];
      const sessionMatch = fm.match(/sourceSession:\s*"([^"]+)"/);
      const msgMatch = fm.match(/sourceMessages:\s*\[([^\]]+)\]/);
      const sessionId = sessionMatch?.[1];
      if (!sessionId) { setSourceState('error'); setTimeout(() => setSourceState('idle'), 2000); return; }
      const messageIds = msgMatch ? msgMatch[1].split(',').map(s => s.trim().replace(/^"|"$/g, '')) : [];
      window.dispatchEvent(new CustomEvent('navigate-to-source-post', { detail: { sessionId, messageIds } }));
      setSourceState('idle');
    } catch {
      setSourceState('error');
      setTimeout(() => setSourceState('idle'), 2000);
    }
  }, [f.path]);

  return (
    <li class={`tdrawer-file${active ? ' tdrawer-file--active' : ''}`} onClick={() => onOpen(f.path)}>
      <CopyBtn filePath={f.path} />
      <ShareBtn filePath={f.path} fileName={f.name} />
      <div class="tdrawer-file-info">
        <span class="tdrawer-file-name">{f.name}</span>
        <span class="tdrawer-file-meta">{formatSize(f.size)} · {timeAgo(f.modifiedAt)}</span>
      </div>
      <button
        class={`post-source-btn${sourceState === 'loading' ? ' loading' : ''}${sourceState === 'error' ? ' error' : ''}`}
        title={sourceState === 'error' ? 'Source not found' : 'Go to source session'}
        onClick={handleSource}
      >
        {sourceState === 'loading' ? '…' : sourceState === 'error' ? '✕' : '↩'}
      </button>
    </li>
  );
}

/** Renders the file list for a single tree group, with collapsible media/ subdirs */
function TreeGroupFiles({
  files,
  onOpen,
  collapsedSubdirs,
  onToggleSubdir,
  groupLabel,
}: {
  files: FileTreeGroup['files'];
  onOpen: (path: string) => void;
  collapsedSubdirs: Record<string, boolean>;
  onToggleSubdir: (key: string) => void;
  groupLabel: string;
}) {
  const nodes = groupBySubdir(files);
  return (
    <>
      {nodes.map((node) => {
        if (node.kind === 'file') {
          return <FileRow key={node.file.path} f={node.file} onOpen={onOpen} />;
        }
        const key = `${groupLabel}/${node.name}`;
        const isCollapsed = collapsedSubdirs[key];
        return (
          <li key={key} class="tdrawer-subdir-group">
            <button class="tdrawer-subdir-header" onClick={() => onToggleSubdir(key)}>
              <span class="tdrawer-tree-chevron">{isCollapsed ? '▶' : '▼'}</span>
              <span class="tdrawer-subdir-name"><FolderIcon /> {node.name}/</span>
              <span class="tdrawer-tree-count">{node.files.length}</span>
            </button>
            {!isCollapsed && (
              <ul class="tdrawer-tree-files tdrawer-subdir-files">
                {node.files.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} />)}
              </ul>
            )}
          </li>
        );
      })}
    </>
  );
}

/* ── Workspace-centric components ──────────────────────────────────────────── */

type WsFile = { name: string; path: string; size: number; modifiedAt: string };

/** Collapsible sub-section inside a project group (Operational Docs, Plans, etc.) */
function WsSubSection({
  icon, label, files, collapsed, onToggle, onOpen, indent = false, activeFilePath, renderRow,
}: {
  icon: ComponentChildren; label: string; files: WsFile[];
  collapsed: boolean; onToggle: () => void;
  onOpen: (p: string) => void; indent?: boolean; activeFilePath?: string;
  renderRow?: (f: WsFile) => ComponentChildren;
}) {
  return (
    <div class={indent ? 'ws-archive-subgroup' : 'ws-subsection'}>
      <button class="ws-subsection-header" onClick={onToggle}>
        <span class="tdrawer-tree-chevron">{collapsed ? '▶' : '▼'}</span>
        <span>{icon} {label}</span>
        <span class="tdrawer-tree-count">{files.length}</span>
      </button>
      {!collapsed && (
        <ul class="tdrawer-list ws-subsection-files">
          {files.map((f) => renderRow ? renderRow(f) : <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}
        </ul>
      )}
    </div>
  );
}

/** Workspace-centric content: workspace root + per-project groups */
function WorkspaceCentricContent({
  workspaceSections, search, refreshToken, onOpen, onConfigurePath, activeFilePath, onUploadClick,
}: {
  workspaceSections: WorkspaceSections;
  search: string; refreshToken: number;
  onOpen: (p: string) => void; onConfigurePath: () => void; activeFilePath: string;
  onUploadClick?: (projectName: string) => void;
}) {
  const { postsDirName, opDocsDirName, plansDirName, projectDocsDirName, registryDirName, uploadsDirName } = workspaceSections;
  const anyConfigured = !!(postsDirName || opDocsDirName || plansDirName || projectDocsDirName || registryDirName || uploadsDirName);

  const [data, setData] = useState<WorkspaceTreeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Class-B restorable view state (Phase 2, #1979) — owned by uiStateStore; the
  // old ad-hoc collapsed-* localStorage keys were folded into its one-time
  // legacy migration (R-A4: no parallel keys).
  const collapsedProjects = useUiState((s) => s.fileDrawerCollapsedProjects);
  const collapsedSections = useUiState((s) => s.fileDrawerCollapsedSections);

  const load = useCallback(async () => {
    if (!anyConfigured) return;
    const p = new URLSearchParams();
    if (postsDirName) p.set('postsDirName', postsDirName);
    if (opDocsDirName) p.set('opDocsDirName', opDocsDirName);
    if (plansDirName) p.set('plansDirName', plansDirName);
    if (projectDocsDirName) p.set('projectDocsDirName', projectDocsDirName);
    if (registryDirName) p.set('registryDirName', registryDirName);
    if (uploadsDirName) p.set('uploadsDirName', uploadsDirName);
    setLoading(true); setError(null);
    try {
      const res = await authFetch(`/api/files/workspace-tree?${p}`);
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      setData(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [postsDirName, opDocsDirName, plansDirName, projectDocsDirName, registryDirName, uploadsDirName, anyConfigured]);

  // Reload whenever refreshToken changes (CLAUDE_DONE or manual ↻)
  useEffect(() => { load(); }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!anyConfigured) return (
    <div class="tdrawer-empty">
      <div class="tdrawer-empty-icon"><FolderIcon /></div>
      <div class="tdrawer-empty-text">No directories configured.</div>
      <button class="tdrawer-configure-btn" onClick={onConfigurePath}>Configure in Settings → Files Drawer</button>
    </div>
  );
  if (loading && !data) return <div class="tdrawer-loading">Loading workspace…</div>;
  if (error) return <div class="tdrawer-error">{error}</div>;
  if (!data) return null;

  const q = search.trim().toLowerCase();
  const match = (f: WsFile) => !q || f.name.toLowerCase().includes(q);

  // Filter workspace root sections
  const wsRoot = {
    posts: (data.workspaceRoot.posts ?? []).filter(match),
    opDocs: data.workspaceRoot.opDocs.filter(match),
    plans: data.workspaceRoot.plans.filter(match),
    projectDocs: data.workspaceRoot.projectDocs.filter(match),
    registry: (data.workspaceRoot.registry ?? []).filter(match),
    archive: data.workspaceRoot.archive.filter(match),
  };
  const hasWsRoot = wsRoot.posts.length + wsRoot.opDocs.length + wsRoot.plans.length + wsRoot.projectDocs.length + wsRoot.registry.length + wsRoot.archive.length > 0;

  // Filter + project-level search
  const visibleProjects = data.projects
    .map((p) => ({
      ...p,
      posts: (p.posts ?? []).filter(match),
      opDocs: p.opDocs.filter(match),
      plans: p.plans.filter(match),
      projectDocs: p.projectDocs.filter(match),
      registry: (p.registry ?? []).filter(match),
      uploads: (p.uploads ?? []).filter(match),
      archivedPlans: p.archivedPlans.filter(match),
      archivedOpDocs: p.archivedOpDocs.filter(match),
      archivedProjectDocs: p.archivedProjectDocs.filter(match),
      archivedOther: p.archivedOther.filter(match),
    }))
    .filter((p) =>
      p.posts.length + p.opDocs.length + p.plans.length + p.projectDocs.length + p.registry.length +
      p.uploads.length +
      p.archivedPlans.length + p.archivedOpDocs.length +
      p.archivedProjectDocs.length + p.archivedOther.length > 0,
    );

  if (!hasWsRoot && visibleProjects.length === 0) return (
    <div class="tdrawer-empty">
      <div class="tdrawer-empty-icon">{q ? '🔍' : <FolderIcon />}</div>
      <div class="tdrawer-empty-text">
        {q ? `No files matching "${search}"` : 'No files found in configured directories'}
      </div>
    </div>
  );

  const toggleProject = (name: string) => toggleFileDrawerCollapsedProject(name);
  const toggleSec = (key: string) => toggleFileDrawerCollapsedSection(key);

  return (
    <div class="ws-content">
      {/* ── Workspace root ── */}
      {hasWsRoot && (
        <div class="ws-root-section">
          <div class="ws-root-header">Workspace</div>
          {wsRoot.posts.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label">📝 {postsDirName || '_posts'}</span>
              <ul class="tdrawer-list">{wsRoot.posts.map((f) => <PostFileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
          {wsRoot.opDocs.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label"><FolderIcon /> {opDocsDirName}</span>
              <ul class="tdrawer-list">{wsRoot.opDocs.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
          {wsRoot.plans.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label">📋 {plansDirName}</span>
              <ul class="tdrawer-list">{wsRoot.plans.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
          {wsRoot.projectDocs.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label">📄 {projectDocsDirName}</span>
              <ul class="tdrawer-list">{wsRoot.projectDocs.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
          {wsRoot.registry.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label">🧪 {registryDirName}</span>
              <ul class="tdrawer-list">{wsRoot.registry.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
          {wsRoot.archive.length > 0 && (
            <div class="ws-root-subsection">
              <span class="ws-root-subsection-label">🗂️ _archive</span>
              <ul class="tdrawer-list">{wsRoot.archive.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}</ul>
            </div>
          )}
        </div>
      )}

      {/* ── Per-project groups ── */}
      {visibleProjects.map((project) => {
        const isCollapsed = !!collapsedProjects[project.name];
        const archiveTotal = project.archivedPlans.length + project.archivedOpDocs.length +
          project.archivedProjectDocs.length + project.archivedOther.length;
        const totalFiles = project.posts.length + project.opDocs.length + project.plans.length +
          project.projectDocs.length + project.registry.length + (project.uploads?.length ?? 0) + archiveTotal;

        return (
          <div key={project.name} class="ws-project-group">
            <button class="ws-project-header" onClick={() => toggleProject(project.name)}>
              <span class="tdrawer-tree-chevron">{isCollapsed ? '▶' : '▼'}</span>
              <span class="ws-project-name">{project.name}</span>
              <span class="tdrawer-tree-count">{totalFiles}</span>
            </button>
            {!isCollapsed && (
              <div class="ws-project-body">
                {project.posts.length > 0 && (
                  <WsSubSection icon="📝" label="Saved Posts" files={project.posts}
                    collapsed={!!collapsedSections[`${project.name}/posts`]}
                    onToggle={() => toggleSec(`${project.name}/posts`)} onOpen={onOpen} activeFilePath={activeFilePath}
                    renderRow={(f) => <PostFileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />}
                  />
                )}
                {project.opDocs.length > 0 && (
                  <WsSubSection icon={<FolderIcon />} label="Operational Docs" files={project.opDocs}
                    collapsed={!!collapsedSections[`${project.name}/op`]}
                    onToggle={() => toggleSec(`${project.name}/op`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                )}
                {project.plans.length > 0 && (
                  <WsSubSection icon="📋" label="Plans" files={project.plans}
                    collapsed={!!collapsedSections[`${project.name}/plans`]}
                    onToggle={() => toggleSec(`${project.name}/plans`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                )}
                {project.projectDocs.length > 0 && (
                  <WsSubSection icon="📄" label="Project Docs" files={project.projectDocs}
                    collapsed={!!collapsedSections[`${project.name}/docs`]}
                    onToggle={() => toggleSec(`${project.name}/docs`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                )}
                {project.registry.length > 0 && (
                  <WsSubSection icon="🧪" label="Registry" files={project.registry}
                    collapsed={!!collapsedSections[`${project.name}/registry`]}
                    onToggle={() => toggleSec(`${project.name}/registry`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                )}
                {/* ── Uploads section (shown when uploadsDirName is configured) ── */}
                {uploadsDirName && (
                  <div class="ws-subsection ws-uploads-group">
                    <div class="ws-uploads-header">
                      <button class="ws-subsection-header" style={{ flex: 1 }}
                        onClick={() => toggleSec(`${project.name}/uploads`)}>
                        <span class="tdrawer-tree-chevron">
                          {collapsedSections[`${project.name}/uploads`] ? '▶' : '▼'}
                        </span>
                        <span>📚 Documents</span>
                        <span class="tdrawer-tree-count">{project.uploads?.length ?? 0}</span>
                      </button>
                      <button
                        class="ws-uploads-add-btn"
                        title="Upload PDF"
                        onClick={(e) => { e.stopPropagation(); onUploadClick?.(project.name); }}
                        aria-label="Upload PDF to this project"
                      >
                        + Upload
                      </button>
                    </div>
                    {!collapsedSections[`${project.name}/uploads`] && (
                      <div class="ws-uploads-body">
                        {(project.uploads?.length ?? 0) === 0 ? (
                          <div class="ws-uploads-empty">No documents yet — upload a PDF</div>
                        ) : (
                          <ul class="tdrawer-list">
                            {(project.uploads ?? []).map((f) => (
                              <UploadFileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} onDeleted={() => load()} />
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {archiveTotal > 0 && (
                  <div class="ws-subsection ws-archive-group">
                    <button class="ws-subsection-header ws-archive-header"
                      onClick={() => toggleSec(`${project.name}/archive`)}>
                      <span class="tdrawer-tree-chevron">{collapsedSections[`${project.name}/archive`] ? '▶' : '▼'}</span>
                      <span>🗂️ Archive</span>
                      <span class="tdrawer-tree-count">{archiveTotal}</span>
                    </button>
                    {!collapsedSections[`${project.name}/archive`] && (
                      <div class="ws-archive-body">
                        {project.archivedOther.length > 0 && (
                          <ul class="tdrawer-list">
                            {project.archivedOther.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} active={f.path === activeFilePath} />)}
                          </ul>
                        )}
                        {project.archivedPlans.length > 0 && (
                          <WsSubSection indent icon="📋" label="Archived Plans" files={project.archivedPlans}
                            collapsed={!!collapsedSections[`${project.name}/arch-plans`]}
                            onToggle={() => toggleSec(`${project.name}/arch-plans`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                        )}
                        {project.archivedOpDocs.length > 0 && (
                          <WsSubSection indent icon={<FolderIcon />} label="Archived Operational Docs" files={project.archivedOpDocs}
                            collapsed={!!collapsedSections[`${project.name}/arch-op`]}
                            onToggle={() => toggleSec(`${project.name}/arch-op`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                        )}
                        {project.archivedProjectDocs.length > 0 && (
                          <WsSubSection indent icon="📄" label="Archived Project Docs" files={project.archivedProjectDocs}
                            collapsed={!!collapsedSections[`${project.name}/arch-docs`]}
                            onToggle={() => toggleSec(`${project.name}/arch-docs`)} onOpen={onOpen} activeFilePath={activeFilePath} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Inner content for a single section (loading, error, file list) */
function SectionContent({
  dirName,
  path,
  emptyIcon,
  configured,
  search,
  refreshToken,
  onOpen,
  onConfigurePath,
  configureHint,
}: {
  dirName?: string;
  path?: string;
  emptyIcon?: string;
  configured: boolean;
  search: string;
  refreshToken: number;
  onOpen: (p: string) => void;
  onConfigurePath: () => void;
  configureHint?: string;
}) {
  const isTree = !!dirName;
  const [flatFiles, setFlatFiles] = useState<FileEntry[]>([]);
  const [treeGroups, setTreeGroups] = useState<FileTreeGroup[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [collapsedSubdirs, setCollapsedSubdirs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isTree) {
        const res = await authFetch(`/api/files/tree?dirName=${encodeURIComponent(dirName!)}`);
        if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
        setTreeGroups(await res.json());
      } else {
        const res = await authFetch(`/api/files/list?path=${encodeURIComponent(path!)}`);
        if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
        setFlatFiles(await res.json());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [isTree, path, dirName]);

  // Load on mount and whenever refreshToken changes (bumped by CLAUDE_DONE or manual ↻)
  useEffect(() => {
    if (configured) load();
  }, [configured, refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply filename search filter
  const q = search.trim().toLowerCase();
  const matchFile = (name: string) => !q || name.toLowerCase().includes(q);
  const filteredFlat = q ? flatFiles.filter((f) => matchFile(f.name)) : flatFiles;
  const filteredTree = q
    ? treeGroups
        .map((g) => ({ ...g, files: g.files.filter((f) => matchFile(f.name)) }))
        .filter((g) => g.files.length > 0)
    : treeGroups;

  const toggleGroup = (label: string) =>
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  const toggleSubdir = (key: string) =>
    setCollapsedSubdirs((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!configured) {
    return (
      <div class="tdrawer-empty">
        <div class="tdrawer-empty-icon"><FolderIcon /></div>
        <div class="tdrawer-empty-text">Not configured.</div>
        <button class="tdrawer-configure-btn" onClick={onConfigurePath}>
          {configureHint || 'Configure in Settings'}
        </button>
      </div>
    );
  }
  if (loading) return <div class="tdrawer-loading">Loading…</div>;
  if (error) return <div class="tdrawer-error">{error}</div>;

  const isEmpty = isTree ? filteredTree.length === 0 : filteredFlat.length === 0;
  if (isEmpty) {
    return (
      <div class="tdrawer-empty">
        <div class="tdrawer-empty-icon">{emptyIcon || '🗒️'}</div>
        <div class="tdrawer-empty-text">
          {q ? `No files matching "${search}"` : isTree ? `No "${dirName}" directories found` : 'No files'}
        </div>
      </div>
    );
  }

  if (isTree) {
    return (
      <ul class="tdrawer-list tdrawer-tree">
        {filteredTree.map((group) => (
          <li key={group.label} class="tdrawer-tree-group">
            <button class="tdrawer-tree-group-header" onClick={() => toggleGroup(group.label)}>
              <span class="tdrawer-tree-chevron">{collapsed[group.label] ? '▶' : '▼'}</span>
              <span class="tdrawer-tree-label">{group.label}</span>
              <span class="tdrawer-tree-count">{group.files.length}</span>
            </button>
            {!collapsed[group.label] && (
              <ul class="tdrawer-tree-files">
                <TreeGroupFiles
                  files={group.files}
                  onOpen={onOpen}
                  collapsedSubdirs={collapsedSubdirs}
                  onToggleSubdir={toggleSubdir}
                  groupLabel={group.label}
                />
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <ul class="tdrawer-list">
        {filteredFlat.map((f) => <FileRow key={f.path} f={f} onOpen={onOpen} />)}
      </ul>
      {path && <div class="tdrawer-footer">{path}</div>}
    </>
  );
}

export function FileListDrawer({ open, path, dirName, title, icon, emptyIcon, onClose, onConfigurePath, configureHint, sections, workspaceSections }: Props) {
  const isMulti = !!sections;

  // Single-section state (kept for backward compat)
  const isTree = !isMulti && !!dirName;
  const configured = isMulti ? true : (isTree ? !!dirName : !!path);

  const [flatFiles, setFlatFiles] = useState<FileEntry[]>([]);
  const [treeGroups, setTreeGroups] = useState<FileTreeGroup[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [collapsedSubdirs, setCollapsedSubdirs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search state — local, cleared when drawer closes
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!open) setSearch(''); }, [open]);

  // Zustand store — refreshToken triggers reload in SectionContent
  const { refreshToken, refresh } = useFileDrawerStore();

  // Upload modal state (workspace-centric mode only — hoisted to satisfy hooks-order rule)
  const [wsUploadModalOpen, setWsUploadModalOpen] = useState(false);
  const [wsUploadModalProject, setWsUploadModalProject] = useState('');
  // Last upload-target project — Class-B restorable view state (Phase 2, #1979),
  // used as the modal's default when no explicit per-project target was clicked.
  const rememberedUploadProject = useUiState((s) => s.fileDrawerUploadProject);

  // unified-drawer P2: swipe-down-to-close. Hook lives above the `if (!open) return null`
  // early return so hook order stays stable across open/closed renders.
  const swipe = useSwipeDownToClose(onClose);

  // Multi-section: which sections are open — Class-B restorable view state owned
  // by uiStateStore (Phase 2, #1979). Merged with the CURRENT sections prop:
  // sections absent from the stored record default to open (the same semantics
  // the folded ad-hoc sections key had).
  const storedOpenSections = useUiState((s) => s.fileDrawerOpenSections);
  const openSections = useMemo<Record<string, boolean>>(() => {
    if (!sections) return {};
    return Object.fromEntries(sections.map((s) => [s.id, storedOpenSections[s.id] ?? true]));
  }, [sections, storedOpenSections]);

  const load = useCallback(async () => {
    if (isMulti) return;
    setLoading(true);
    setError(null);
    try {
      if (isTree) {
        const res = await authFetch(`/api/files/tree?dirName=${encodeURIComponent(dirName!)}`);
        if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
        setTreeGroups(await res.json());
      } else {
        const res = await authFetch(`/api/files/list?path=${encodeURIComponent(path!)}`);
        if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
        setFlatFiles(await res.json());
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [isMulti, isTree, path, dirName]);

  useEffect(() => {
    if (!isMulti && open && configured) load();
  }, [isMulti, open, configured, load]);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Restore scroll position when workspace-centric drawer opens (from uiStateStore)
  useEffect(() => {
    if (!workspaceSections || !open) return;
    const el = scrollRef.current;
    if (!el) return;
    const saved = getUiState().fileDrawerScroll;
    if (saved > 0) el.scrollTop = saved;
  }, [open, workspaceSections]);

  // Save scroll position on scroll (debounced) — named store action only.
  useEffect(() => {
    if (!workspaceSections || !open) return;
    const el = scrollRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setFileDrawerScroll(el.scrollTop);
      }, 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [open, workspaceSections]);

  const openFile = (filePath: string) => {
    try { sessionStorage.setItem('files-drawer-active-file', filePath); } catch {}
    window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: filePath } }));
    onClose();
  };

  const toggleGroup = (label: string) =>
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  const toggleSubdir = (key: string) =>
    setCollapsedSubdirs((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleSection = (id: string) =>
    setFileDrawerOpenSections({ ...openSections, [id]: !openSections[id] });
  const collapseAll = () =>
    setFileDrawerOpenSections(Object.fromEntries(Object.keys(openSections).map((k) => [k, false])));

  if (!open) return null;

  // ── Workspace-centric mode ────────────────────────────────────────────────
  if (workspaceSections) {
    return (
      <div class="tdrawer-overlay" onClick={onClose}>
        <div class="tdrawer-panel" onClick={(e) => e.stopPropagation()}>
          <div class="drawer-grab" aria-hidden="true" onTouchStart={swipe.onTouchStart} onTouchEnd={swipe.onTouchEnd} />
          <div class="tdrawer-header">
            <span class="tdrawer-title">{icon} {title}</span>
            <div class="tdrawer-header-actions">
              <button class="tdrawer-refresh" onClick={refresh} title="Refresh">↻</button>
              <button class="tdrawer-close" onClick={onClose} title="Close">✕</button>
            </div>
          </div>
          <div class="tdrawer-search-bar">
            <input
              class="tdrawer-search-input"
              type="search"
              placeholder="Search by filename…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
            {search && (
              <button class="tdrawer-search-clear" onClick={() => setSearch('')} title="Clear">✕</button>
            )}
          </div>
          <div class="tdrawer-multi-sections" ref={scrollRef}>
            <WorkspaceCentricContent
              workspaceSections={workspaceSections}
              search={search}
              refreshToken={refreshToken}
              onOpen={openFile}
              onConfigurePath={() => { onClose(); onConfigurePath(); }}
              activeFilePath={sessionStorage.getItem('files-drawer-active-file') || ''}
              onUploadClick={(projectName) => {
                setWsUploadModalProject(projectName);
                setFileDrawerUploadProject(projectName);
                setWsUploadModalOpen(true);
              }}
            />
          </div>
        </div>
        {wsUploadModalOpen && (
          <UploadModal
            open={wsUploadModalOpen}
            onClose={() => setWsUploadModalOpen(false)}
            defaultProject={wsUploadModalProject || rememberedUploadProject}
            onUploaded={(markdownPath: string) => {
              setWsUploadModalOpen(false);
              refresh();
              openFile(markdownPath);
            }}
          />
        )}
      </div>
    );
  }

  const isEmpty = isTree ? treeGroups.length === 0 : flatFiles.length === 0;

  // ── Multi-section mode ──────────────────────────────────────────────────────
  if (isMulti && sections) {
    return (
      <div class="tdrawer-overlay" onClick={onClose}>
        <div class="tdrawer-panel" onClick={(e) => e.stopPropagation()}>
          <div class="drawer-grab" aria-hidden="true" onTouchStart={swipe.onTouchStart} onTouchEnd={swipe.onTouchEnd} />
          <div class="tdrawer-header">
            <span class="tdrawer-title">{icon} {title}</span>
            <div class="tdrawer-header-actions">
              <button class="tdrawer-refresh" onClick={refresh} title="Refresh all sections">↻</button>
              <button class="tdrawer-collapse-all" onClick={collapseAll} title="Collapse all sections">Collapse all</button>
              <button class="tdrawer-close" onClick={onClose} title="Close">✕</button>
            </div>
          </div>
          <div class="tdrawer-search-bar">
            <input
              ref={searchRef}
              class="tdrawer-search-input"
              type="search"
              placeholder="Search by filename…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
            {search && (
              <button class="tdrawer-search-clear" onClick={() => setSearch('')} title="Clear search">✕</button>
            )}
          </div>
          <div class="tdrawer-multi-sections">
            {sections.map((sec) => {
              const secConfigured = !!(sec.dirName || sec.path);
              return (
                <div key={sec.id} class="tdrawer-section">
                  <button
                    class="tdrawer-section-header"
                    onClick={() => toggleSection(sec.id)}
                  >
                    <span class="tdrawer-tree-chevron">{openSections[sec.id] ? '▼' : '▶'}</span>
                    <span class="tdrawer-section-title">{sec.icon} {sec.title}</span>
                  </button>
                  {openSections[sec.id] && (
                    <div class="tdrawer-section-body">
                      <SectionContent
                        dirName={sec.dirName}
                        path={sec.path}
                        emptyIcon={sec.emptyIcon}
                        configured={secConfigured}
                        search={search}
                        refreshToken={refreshToken}
                        onOpen={openFile}
                        onConfigurePath={onConfigurePath}
                        configureHint={sec.configureHint}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Single-section mode (legacy) ─────────────────────────────────────────────
  return (
    <div class="tdrawer-overlay" onClick={onClose}>
      <div class="tdrawer-panel" onClick={(e) => e.stopPropagation()}>
        <div class="drawer-grab" aria-hidden="true" onTouchStart={swipe.onTouchStart} onTouchEnd={swipe.onTouchEnd} />
        <div class="tdrawer-header">
          <span class="tdrawer-title">{icon} {title}</span>
          <div class="tdrawer-header-actions">
            {configured && (
              <button class="tdrawer-refresh" onClick={load} title="Refresh" disabled={loading}>
                ↻
              </button>
            )}
            <button class="tdrawer-close" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        {!configured ? (
          <div class="tdrawer-empty">
            <div class="tdrawer-empty-icon"><FolderIcon /></div>
            <div class="tdrawer-empty-text">No folder configured.</div>
            <button class="tdrawer-configure-btn" onClick={() => { onClose(); onConfigurePath(); }}>
              {configureHint || 'Configure in Settings'}
            </button>
          </div>
        ) : loading ? (
          <div class="tdrawer-loading">Loading…</div>
        ) : error ? (
          <div class="tdrawer-error">{error}</div>
        ) : isEmpty ? (
          <div class="tdrawer-empty">
            <div class="tdrawer-empty-icon">{emptyIcon || '🗒️'}</div>
            <div class="tdrawer-empty-text">
              {isTree ? `No "${dirName}" directories found in workspace` : 'No files'}
            </div>
          </div>
        ) : isTree ? (
          <ul class="tdrawer-list tdrawer-tree">
            {treeGroups.map((group) => (
              <li key={group.label} class="tdrawer-tree-group">
                <button
                  class="tdrawer-tree-group-header"
                  onClick={() => toggleGroup(group.label)}
                >
                  <span class="tdrawer-tree-chevron">{collapsed[group.label] ? '▶' : '▼'}</span>
                  <span class="tdrawer-tree-label">{group.label}</span>
                  <span class="tdrawer-tree-count">{group.files.length}</span>
                </button>
                {!collapsed[group.label] && (
                  <ul class="tdrawer-tree-files">
                    <TreeGroupFiles
                      files={group.files}
                      onOpen={openFile}
                      collapsedSubdirs={collapsedSubdirs}
                      onToggleSubdir={toggleSubdir}
                      groupLabel={group.label}
                    />
                  </ul>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul class="tdrawer-list">
            {flatFiles.map((f) => (
              <FileRow key={f.path} f={f} onOpen={openFile} />
            ))}
          </ul>
        )}

        {!isTree && path && (
          <div class="tdrawer-footer">{path}</div>
        )}
      </div>
    </div>
  );
}
